import { useActiveBeat, useOriginals } from 'hooks';
import React, { useState, createContext, useEffect, useCallback } from 'react';
import orderBy from 'lodash/orderBy';
import { Original, PlaylistChoices, Sort } from '../common';
import { QueryStatus } from 'react-query';
import { useHistory } from 'react-router';
import { getDropFromContractAddress } from 'utils/dropsHelpers';
import { useMemo } from 'react';
import { useReducer } from 'react';
import { useWalletItemsContext } from 'components/WalletItemsContext';

interface PlayerContextValue {
  activeBeat: Original | null;
  activeIndex: number | null;
  autoPlayNext: boolean;
  playlist: Original[];
  selectedPlaylist: PlaylistChoices;
  setSelectedPlaylist: any;
  toggleSort: any;
  priceSort: Sort | null;
  status: QueryStatus;
  dispatch: any;
}

interface PlayerState {
  activeIndex: number | null;
  activeBeat: Original | null;
  playlist: Original[];
  autoPlayNext: boolean;
}

type PlayerActions =
  | {
      type: 'select';
      beat?: Original;
      id?: string | number;
      activeIndex?: number;
      autoPlayNext?: boolean;
    }
  | { type: 'next'; fromIndex?: number; autoPlayNext?: boolean }
  | { type: 'previous'; autoPlayNext?: boolean }
  | { type: 'setActiveIndex'; activeIndex: number }
  | { type: 'setPlaylist'; playlist: Original[] }
  | { type: 'stop' };

const PlayerContext = createContext<PlayerContextValue>({
  activeBeat: null,
  activeIndex: null,
  autoPlayNext: false,
  dispatch: () => {},
  playlist: [],
  selectedPlaylist: PlaylistChoices.default,
  setSelectedPlaylist: () => {},
  toggleSort: () => {},
  priceSort: null,
  status: 'idle',
});

const iOS =
  ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
    navigator.platform
  ) ||
  // iPad on iOS 13 detection
  (navigator.userAgent.includes('Mac') && 'ontouchend' in document);

function reducer(state: PlayerState, action: PlayerActions): PlayerState {
  console.log('action', action);
  console.log('state', state);

  switch (action.type) {
    case 'select':
      if (action.beat) {
        const activeIndex =
          action.activeIndex ?? state.playlist.findIndex(e => e.id === action.beat!.id);
        return {
          ...state,
          activeIndex,
          activeBeat: action.beat,
          autoPlayNext: Boolean(!iOS && action.autoPlayNext),
        };
      } else if (action.id) {
        const activeIndex = state.playlist.findIndex(e => e.id === action.id);
        return {
          ...state,
          activeIndex,
          activeBeat: state.playlist[activeIndex],
          autoPlayNext: Boolean(!iOS && action.autoPlayNext),
        };
      } else {
        return state;
      }
    case 'next': {
      let nextIndex = (action.fromIndex ?? state.activeIndex ?? -1) + 1;

      if (nextIndex >= state.playlist.length) {
        nextIndex = 0;
      }

      const newBeat = state.playlist[nextIndex];

      if (newBeat) {
        return {
          ...state,
          activeIndex: nextIndex,
          activeBeat: newBeat,
          autoPlayNext: Boolean(!iOS && action.autoPlayNext),
        };
      } else {
        return state;
      }
    }
    case 'previous': {
      let nextIndex = (state.activeIndex ?? 0) - 1;
      if (nextIndex < 0) {
        nextIndex = state.playlist.length - 1;
      }

      const newBeat = state.playlist[nextIndex];

      if (newBeat) {
        return {
          ...state,
          activeIndex: nextIndex,
          activeBeat: newBeat,
          autoPlayNext: Boolean(!iOS && action.autoPlayNext),
        };
      } else {
        return state;
      }
    }
    case 'setActiveIndex':
      return {
        ...state,
        activeIndex: action.activeIndex,
      };
    case 'setPlaylist': {
      let activeIndex = state.activeIndex;

      // Update index when changing playlist so correct beat row is highlighted
      const indexIsOutOfBounds =
        state.activeIndex && state.activeIndex > action.playlist.length - 1;

      if (!state.activeIndex || !state.activeBeat) {
        activeIndex = null;
      } else if (
        indexIsOutOfBounds ||
        action.playlist[state.activeIndex].id !== state.activeBeat.id
      ) {
        activeIndex = action.playlist.findIndex(e => e.id === state.activeBeat!.id);
        if (activeIndex === -1) {
          activeIndex = null;
        }
      }

      return {
        ...state,
        activeIndex,
        playlist: action.playlist,
      };
    }
    case 'stop':
      if (state.autoPlayNext) {
        return {
          ...state,
          autoPlayNext: false,
        };
      } else {
        return state;
      }
    default:
      throw new Error();
  }
}

const PlayerContextProvider = ({ children }: any) => {
  const { activeBeat, id: activeBeatId } = useActiveBeat();

  const [state, dispatch] = useReducer(reducer, {
    playlist: [],
    activeIndex: null,
    activeBeat,
    autoPlayNext: false,
  });

  const [priceSort, setPriceSort] = useState<Sort | null>(null);
  const [selectedPlaylist, setSelectedPlaylist] = useState<PlaylistChoices>(
    PlaylistChoices.default
  );

  const { activeDropOriginals, status: defaultStatus } = useOriginals();

  const { ownedItems, status: ownedStatus } = useWalletItemsContext();

  const activeDropOwnedPlaylist = useMemo(() => {
    const ownedItemOriginalIds = ownedItems
      .filter(item => item.original?.id)
      .map(item => item.original?.id);
    return activeDropOriginals?.filter(item => ownedItemOriginalIds.includes(item.id));
  }, [activeDropOriginals, ownedItems]);

  const history = useHistory();

  const setActiveBeatRoute = useCallback(
    (beat: Original) => {
      const { id, contractAddress } = beat;
      const drop = getDropFromContractAddress(contractAddress);
      history.replace(`/${drop}/${id}`);
    },
    [history]
  );

  useEffect(() => {
    if (state.activeBeat && activeBeatId !== state.activeBeat.id) {
      setActiveBeatRoute(state.activeBeat);
    }
  }, [state.activeBeat, activeBeatId, setActiveBeatRoute]);

  // This set active beat on initial load of route with beat id (ex. on page refresh)
  useEffect(() => {
    if (!state.activeBeat && activeBeatId && state.playlist.length) {
      dispatch({ type: 'select', id: activeBeatId });
    }
  }, [state.activeBeat, activeBeatId, state.playlist]);

  const sort = useCallback(
    (tokens: Original[]) => {
      if (priceSort) {
        const sorted = orderBy(tokens, 'print.supply', priceSort);
        return sorted;
      } else {
        const defaultSorted = orderBy(tokens, 'mintNumber');
        return defaultSorted;
      }
    },
    [priceSort]
  );

  useEffect(() => {
    if (selectedPlaylist === PlaylistChoices.default) {
      console.log('activeDropOriginals', activeDropOriginals);
      const newPlaylist = sort(activeDropOriginals);
      dispatch({ type: 'setPlaylist', playlist: newPlaylist });
    } else if (selectedPlaylist === PlaylistChoices.owned) {
      console.log('activeDropOwnedPlaylist', activeDropOwnedPlaylist);
      const newPlaylist = sort(activeDropOwnedPlaylist);
      dispatch({ type: 'setPlaylist', playlist: newPlaylist });
    }
  }, [selectedPlaylist, activeDropOriginals, activeDropOwnedPlaylist, sort]);

  const toggleSort = () => {
    setPriceSort(curr => {
      switch (curr) {
        case Sort.asc:
          return Sort.desc;
        case Sort.desc:
          return null;
        default:
          return Sort.asc;
      }
    });
  };

  useEffect(() => {
    if (!activeBeatId && state.playlist && state.playlist.length > 0) {
      const newBeat = state.playlist[0];
      console.log(`select ${newBeat}`);
      if (newBeat) {
        dispatch({
          type: 'select',
          beat: newBeat,
          activeIndex: 0,
        });
      }
    }
  }, [selectedPlaylist, activeBeatId, state]);

  const contextValue = useMemo(
    () => ({
      selectedPlaylist,
      setSelectedPlaylist,
      toggleSort,
      priceSort,
      status: selectedPlaylist === PlaylistChoices.owned ? ownedStatus : defaultStatus, // maybe this should just be or
      dispatch,
      ...state,
    }),
    [selectedPlaylist, setSelectedPlaylist, priceSort, defaultStatus, ownedStatus, state]
  );

  return <PlayerContext.Provider value={contextValue}>{children}</PlayerContext.Provider>;
};

const usePlayerContext = () => {
  const context = React.useContext(PlayerContext);
  if (context === undefined) {
    throw new Error('useWeb3Context must be used within a PlayerContextProvider');
  }
  return context;
};

export { PlayerContextProvider, usePlayerContext };
